<template>
  <div :class="[
       'main-menu',
       'menu-fixed',
       'menu-accordion',
       'menu-shadow',
       isCollapsed ? null : 'expanded',
       active.is.over && isCollapsed ? 'expanded' : null,
       'menu-light']"
       @mouseenter="onOver(true)"
       @mouseleave="onOver(false)">
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot name="header">
        <ul class="nav navbar-nav flex-row justify-content-between">
          <li class="nav-item mr-auto">
            <router-link
              :to="{ name: getToggle}"
              class="navbar-brand router-link-active" target="_self">
              <span class="brand-logo">
                <img :src="getBrandLogo"
                     alt="logo"/>
              </span>
            </router-link>
          </li>
          <!-- 使用者 (帳號/邀請碼/主題/語言/登出 皆在此下拉); 與 logo 同層靠右, 收合時隱藏 -->
          <j-menu-user class="sidebar-account" />
          <li class="nav-item nav-toggle">
            <a v-if="!props.isMinimal"
               @click="setCollapsed(!isCollapsed)"
               class="cgBase nav-link modern-nav-toggle">
              <icon-sax v-if="isCollapsed"
                        class="ft-20"
                        type="sidebar-right"/>
              <icon-sax v-else
                        class="ft-20"
                        type="sidebar-left"/>
            </a>
            <a v-else
               @click="setOverlay(false)"
               class="cgBase nav-link modern-nav-toggle">
              <icon-sax class="ft-20"
                        type="x"/>
            </a>
          </li>
        </ul>
      </slot>
    </div>
    <div :style="{
      display: active.is.top ? 'none' : 'block'
    }" class="shadow-bottom"/>
    <el-scrollbar
      @scroll="onScroll"
      height="100%"
      class="ps-container main-menu-content scroll-area ps ps--active-y">
      <vertical-menu-item
        :items="getItem"
        class="navigation navigation-main"/>

      <div v-if="!isCollapsed" class="w-100 text-center power">
        <span class="text-success">
          <span class="text-muted ks ft-13" style="text-align: center"
                v-safe-html="getDescription"/>
        </span>
      </div>
    </el-scrollbar>
  </div>
</template>

<script setup>

import IconSax from "@/component/IconSax.vue"
import VerticalMenuItem from "@/component/menu/vertical/item/VerticalMenuItem.vue"

import JMenuUser from "@/component/menu/horizontal/item/HorizontalMenuUser.vue"

import {reactive, onMounted, computed} from "vue"
import {useNavigationItem} from "@/navigation"
import {useMenu} from "@/hook/useMenu"
import {useRoute} from "vue-router"
import {usePermission} from "@/hook/usePermission"
import http from "@/api"
import {useUser} from "@/hook/useUser"
import {EnumPortalDashboard, EnumPortalName, EnumPortalRouter} from "@/enum/portal";
import {sprintf} from "sprintf-js";
import {useI18n} from "vue-i18n";

/**
 * API（僅本元件使用 → 內聯實作，不另立 api/module）：抬頭 GET /api/caption（不顯示讀取遮罩）
 */
const apiCaption = () =>
    http.get(`/api/caption`, {}, {opts: {builtIn: {loadingMask: false}}})

const {
  t
} = useI18n()

const {
  isCollapsed,
  setOverlay,
  setCollapsed
} = useMenu()

const {
  getUser,
  setUserCaption,
} = useUser()

const {
  getMenuItem
} = useNavigationItem()

const {
  isHead
} = usePermission()

const route = useRoute()

const props = defineProps({
    isMinimal: { type: Boolean, required: false, default: false }
  })

//本地端控制參數
const active = reactive({
  is: {
    top: true,
    over: false
  }
})

//回傳滑鼠是否正滑過, 滑過展開功能表, 移除關閉功能表
const onOver = (isOver) => active.is.over = isOver

//選單捲動
const onScroll = (e) => {
  if (e === undefined) active.is.top = true
  else active.is.top = e.scrollTop === 0
}

//返回本系統首頁的路由名 (logo 點下去回哪裡)
const getToggle = computed(() => {
  return EnumPortalDashboard[route.meta.portal] ?? EnumPortalRouter.ADMIN
})

//獲取項目
const getItem = computed(() => {
  //榮耀堂首頁: 沒有公吿欄, 也沒有心靈捕手 / 每日靈糧的委派, 下面兩段過濾都不適用
  if (route.meta.portal === EnumPortalName.GLORY) return getMenuItem.value
  //總教會不出現的項目 (不受權限控制的頁面)
  if (isHead.value) return getMenuItem.value.filter(i => ![
    `Billboard`
  ].includes(i.route))
  return getMenuItem.value.filter(i => {
    //心靈捕手自行維護, 不顯示左側選單
    if (getUser.value.church.delegate.soul && i.id === 6) return false
    //每日靈糧自行維護, 不顯示左側選單
    else if (getUser.value.church.delegate.daily && i.id === 7) return false
    return true
  })
})

//獲取機構圖
const getBrandLogo = computed(() => {
  return "/img/organization/1/logo.png"
})

const initial = async () => {

  // v2不執行取得標題功能
  if (route.meta?.v2 ?? false) return

  const {
    data
  } = await apiCaption()

  //注入用戶資訊
  if (data?.data) setUserCaption(data.data)

}
const getDescription = computed(() => {
  //
  return sprintf(
    t('系統由中華愛無界全人關懷協會提供和維護使用教學'),
    import.meta.env.VITE_SITE_SITE,
    import.meta.env.VITE_SITE_TEACH
  )
})
onMounted(() => {
  onScroll()
  initial()
})

//主動暴露
defineExpose({
  initial
})

</script>

<style scoped lang="scss">
.nav {
  .modern-nav-toggle {
    margin-top: 1.7rem;
    margin-right: .1rem;

  }
}
.main-menu {
  .navbar-header {
    padding-left: 22px !important;
    padding-top: 0 !important;
    z-index: 3;

    .brand-logo {
      > img {
        border-radius: 0.375rem;

      }
    }
  }
}
// 使用者頭像移到 logo 同層 (靠右、toggle 左邊); logo 的 mr-auto 會把頭像+toggle 推到右側成一組
.sidebar-account {
  display: flex;
  align-items: center;
  // 放大頭像後上移對齊 logo 中線; navbar-header 固定 4.45rem, 此區高度不變
  margin-top: 0.7rem;
  margin-right: 0.6rem;
  list-style: none;
  :deep(.dropdown-user-link) {
    padding: 0 !important;
    display: flex;
    align-items: center;
  }
  // 設計感: 頭像圓形外加細環, hover 提亮為主題色
  :deep(.hd) {
    padding: 2px;
    border-radius: 50%;
    border: 2px solid rgba(115, 103, 240, 0.35);
    transition: border-color .18s ease;
  }
  :deep(.dropdown-user-link:hover .hd) {
    border-color: rgba(115, 103, 240, 0.9);
  }
  :deep(.avatar-s.avatar-normal) {
    width: 44px !important;
    height: 44px !important;
    border-radius: 50% !important;
  }
  // 隱藏使用者下拉的下三角
  :deep(.dropdown-toggle::after) {
    display: none;

  }
}
// 收合靜置 (74px 窄欄) 時隱藏頭像, 僅留 logo + 展開鈕; 展開或 hover 展開時才顯示
.main-menu:not(.expanded) .sidebar-account {
  display: none;

}
</style>
